import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext'; // Certifiquei que é useAuth
import { collection, addDoc, getDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription 
} from "../components/ui/dialog";
import { 
  ArrowLeft, Search, MapPin, Send, Home, Clock, CheckCircle2, X, User, ShieldCheck, Loader2, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/ui/Navbar';
import planoFundo from "../assets/planodefundo.png"; 

const DirectOrder = () => {
  const { techId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, authLoading } = useAuth(); // CORRIGIDO: Era userAuth
  
  const [loading, setLoading] = useState(false);
  const [techInfo, setTechInfo] = useState(null);
  const [editLocation, setEditLocation] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [validatingCep, setValidatingCep] = useState(false);
  
  const [formData, setFormData] = useState({
    serviceArea: location.state?.selectedArea || '',
    description: '',
    cep: '',
    address: '',
    neighborhood: '',
    city: '',
    state: '',
    number: '',
    complement: '',
    propertyType: 'casa',
    urgency: 'normal'
  });

  const sendNotification = async (targetUserId, title, message, type, category, link = "") => {
    try {
      await addDoc(collection(db, 'notifications'), {
        userId: targetUserId,
        title,
        message,
        type, 
        category,
        link,
        read: false,
        createdAt: serverTimestamp()
      });
    } catch (error) { console.error("Erro notificação:", error); }
  };

  useEffect(() => {
    const fetchTech = async () => {
      try {
        const docSnap = await getDoc(doc(db, "users", techId));
        if (docSnap.exists()) {
          const data = docSnap.data();
          setTechInfo(data);
          const techAreas = data.professionalData?.areas || data.areas || [];
          if (techAreas.length === 1 && !formData.serviceArea) {
            setFormData(prev => ({ ...prev, serviceArea: techAreas[0] }));
          }
        } else {
          toast.error("Técnico não localizado.");
          navigate('/dashboard');
        }
      } catch (e) { console.error(e); }
    };
    if (techId) fetchTech();
  }, [techId, navigate, formData.serviceArea]);

  // PUXA O ENDEREÇO DO CADASTRO INICIALMENTE
  useEffect(() => {
    if (userProfile?.addressDetails) {
      const addr = userProfile.addressDetails;
      setFormData(prev => ({
        ...prev,
        cep: addr.cep || '',
        address: addr.street || '',
        neighborhood: addr.neighborhood || '',
        city: addr.city || '',
        state: addr.state || '',
        number: addr.number || '',
        complement: addr.complement || ''
      }));
    }
  }, [userProfile]);

  const handleLookupCep = useCallback(async (cep) => {
    setValidatingCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      
      if (data.erro) {
        toast.error('CEP não localizado!');
        return;
      }

      const techCity = (techInfo?.professionalData?.address?.city || techInfo?.addressDetails?.city || "").toUpperCase();
      const clientCity = (data.localidade || "").toUpperCase();

      if (techCity && clientCity !== techCity) {
        toast.error(`Este técnico não atende na cidade do CEP informado.`);
        setFormData(prev => ({ ...prev, cep: '' })); 
        return;
      }

      setFormData(prev => ({
        ...prev, 
        address: data.logradouro ? data.logradouro.toUpperCase() : '', 
        neighborhood: data.bairro ? data.bairro.toUpperCase() : 'CENTRO', 
        city: clientCity, 
        state: data.uf.toUpperCase(),
        cep: cep
      }));

    } catch (error) { toast.error('Erro ao validar CEP.'); }
    finally { setValidatingCep(false); }
  }, [techInfo]);

  useEffect(() => {
    const cleanCep = formData.cep.replace(/\D/g, '');
    if (cleanCep.length === 8) {
      handleLookupCep(cleanCep);
    }
  }, [formData.cep, handleLookupCep]);

  const handleFinalSubmit = async () => {
    if (!formData.serviceArea) return toast.error("Selecione o serviço.");
    if (!formData.address || !formData.city) return toast.error("Endereço incompleto.");
    
    setLoading(true);
    const serviceCode = Math.random().toString(36).substring(2, 8).toUpperCase();
    const fullLocationString = `${formData.address}, ${formData.number}${formData.complement ? ` (${formData.complement})` : ''}${formData.neighborhood ? ` - ${formData.neighborhood}` : ''}, ${formData.city}/${formData.state}`;

    try {
      await addDoc(collection(db, 'orders'), {
        address: fullLocationString,
        clientId: user.uid,
        clientName: userProfile?.name || 'Cliente',
        clientPhoto: userProfile?.photoURL || '',
        createdAt: new Date().toISOString(),
        description: formData.description,
        propertyType: formData.propertyType,
        serviceArea: formData.serviceArea,
        serviceCode: serviceCode,
        status: 'waiting_approval', 
        technicianId: techId,
        technicianName: techInfo?.professionalData?.name || techInfo?.name || 'Técnico',
        type: 'direct',
        updatedAt: new Date().toISOString(),
        urgency: formData.urgency,
        addressDetails: { ...formData }
      });

      const isUrgent = formData.urgency === 'emergency' || formData.urgency === 'high';
      await sendNotification(
        techId,
        isUrgent ? "🚨 CHAMADO URGENTE!" : "Novo Pedido Direto! 🛠️",
        `${userProfile?.name} enviou uma solicitação de ${formData.serviceArea}. Verifique os detalhes.`,
        "technician",
        isUrgent ? "urgent" : "order",
        "/technician-dashboard"
      );

      setShowConfirmModal(false);
      toast.success(`Solicitação enviada!`);
      navigate('/history');
    } catch (error) { toast.error('Erro ao enviar pedido.'); } 
    finally { setLoading(false); }
  };

  if (authLoading || !techInfo) return <div className="h-screen flex items-center justify-center bg-white"><Loader2 className="animate-spin text-blue-600" /></div>;

  return (
    <div className="relative min-h-screen font-sans text-slate-900 overflow-x-hidden text-left">
      <div className="fixed inset-0 -z-10 w-full h-full" style={{ backgroundImage: `url(${planoFundo})`, backgroundSize: 'cover', backgroundPosition: 'center' }}><div className="absolute inset-0 bg-slate-900/60" /></div>
      <Navbar />
      
      <main className="relative z-10 max-w-4xl mx-auto px-6 pt-32 pb-12">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6 rounded-full font-black text-[10px] uppercase italic text-white hover:bg-white/10">
          <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
        </Button>

        <div className="bg-white/95 rounded-[2.5rem] shadow-2xl overflow-hidden border border-white/20">
          <div className="bg-slate-900 p-8 text-white flex items-center gap-6 border-b-8 border-orange-500">
              <div className="w-16 h-16 rounded-2xl overflow-hidden border-2 border-orange-500 bg-slate-800 shrink-0">
                  {techInfo.professionalData?.photoUrl ? <img src={techInfo.professionalData.photoUrl} className="w-full h-full object-cover" alt="" /> : <User className="w-full h-full p-3 text-slate-600" />}
              </div>
              <div className="text-left flex-1">
                 <h2 className="text-xl font-black uppercase italic leading-none mb-1 text-white">Solicitação Direta</h2>
                 <p className="text-orange-400 text-[10px] font-black uppercase tracking-widest italic">{techInfo.professionalData?.name || techInfo.name}</p>
              </div>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); setShowConfirmModal(true); }} className="p-8 md:p-12 space-y-10">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase text-slate-400 ml-2 italic">Especialidade Desejada</Label>
                <Select value={formData.serviceArea} onValueChange={(v) => setFormData({...formData, serviceArea: v})}>
                  <SelectTrigger className="h-16 rounded-2xl bg-slate-50 border-none font-bold uppercase italic shadow-inner">
                    <div className="flex items-center gap-2"><Search className="w-4 h-4 text-blue-600"/><SelectValue placeholder="Selecione..." /></div>
                  </SelectTrigger>
                  <SelectContent className="bg-white z-[500]">
                    {(techInfo.professionalData?.areas || techInfo.areas || []).map((area, i) => (
                        <SelectItem key={i} value={area} className="font-bold uppercase italic text-[11px]">{area}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase text-slate-400 ml-2 italic">Tipo de Local</Label>
                <Select value={formData.propertyType} onValueChange={(v) => setFormData({...formData, propertyType: v})}>
                  <SelectTrigger className="h-16 rounded-2xl bg-slate-50 border-none font-bold uppercase italic shadow-inner">
                    <div className="flex items-center gap-2"><Home className="w-4 h-4 text-blue-600"/><SelectValue /></div>
                  </SelectTrigger>
                  <SelectContent className="bg-white z-[500]">
                    <SelectItem value="casa">Casa</SelectItem>
                    <SelectItem value="apartamento">Apartamento</SelectItem>
                    <SelectItem value="comercial">Comercial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase text-slate-400 ml-2 italic">O que precisa ser feito?</Label>
              <Textarea placeholder="Detalhes do problema..." className="min-h-[140px] rounded-[2rem] bg-slate-50 border-none font-bold p-6 shadow-inner text-left" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required />
            </div>

            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase text-slate-400 ml-2 italic">Nível de Urgência</Label>
              <Select value={formData.urgency} onValueChange={(v) => setFormData({...formData, urgency: v})}>
                <SelectTrigger className="h-14 rounded-2xl bg-slate-50 border-none font-black uppercase italic shadow-inner">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white z-[500]">
                  <SelectItem value="low">Baixa</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">Alta</SelectItem>
                  <SelectItem value="emergency" className="text-red-600 font-black">Emergência</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="pt-6 border-t border-slate-100 text-left">
              <div className="flex justify-between items-center mb-4 ml-2">
                <Label className="text-[10px] font-black uppercase text-slate-400 italic text-left">Endereço de Atendimento</Label>
                <button type="button" onClick={() => setEditLocation(!editLocation)} className="text-[10px] font-black text-blue-600 uppercase italic underline underline-offset-4">{editLocation ? "Ok" : "Alterar"}</button>
              </div>

              {!editLocation ? (
                <div className="p-6 bg-blue-50/50 rounded-3xl border border-dashed border-blue-200 flex items-center gap-4 text-left">
                  <MapPin className="text-red-500 shrink-0" size={24} />
                  <p className="font-bold text-slate-700 italic text-sm">
                    {formData.address}, {formData.number} {formData.complement && `(${formData.complement})`} <br/>
                    {formData.neighborhood} - {formData.city}/{formData.state}
                  </p>
                </div>
              ) : (
                <div className="space-y-4 animate-in slide-in-from-top-2 text-left">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative">
                      <Input placeholder="CEP" onChange={e => setFormData({...formData, cep: e.target.value})} value={formData.cep} className="h-14 rounded-2xl font-bold bg-slate-50 border-none shadow-inner" />
                      {validatingCep && <Loader2 className="absolute right-3 top-4 animate-spin text-orange-500 w-5 h-5" />}
                    </div>
                    <Input placeholder="Número" onChange={e => setFormData({...formData, number: e.target.value})} value={formData.number} className="h-14 rounded-2xl font-bold bg-slate-50 border-none shadow-inner" />
                  </div>
                  
                  <Input 
                    placeholder="Logradouro" 
                    value={formData.address} 
                    onChange={e => setFormData({...formData, address: e.target.value.toUpperCase()})}
                    disabled={formData.address !== '' && !validatingCep}
                    className={`h-14 rounded-2xl font-bold uppercase italic shadow-inner border-none ${formData.address ? 'bg-slate-100' : 'bg-orange-50 animate-pulse'}`} 
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <Input placeholder="Bairro" value={formData.neighborhood} disabled className="h-14 rounded-2xl font-bold bg-slate-100 border-none italic opacity-70" />
                    <Input placeholder="Cidade" value={formData.city} disabled className="h-14 rounded-2xl font-black bg-slate-100 border-none italic opacity-70 text-blue-600" />
                  </div>
                  <Input placeholder="Complemento" value={formData.complement} onChange={e => setFormData({...formData, complement: e.target.value.toUpperCase()})} className="h-14 rounded-2xl font-bold bg-slate-50 border-none shadow-inner" />
                </div>
              )}
            </div>

            <Button type="submit" disabled={loading || validatingCep} className="w-full h-20 bg-orange-600 hover:bg-orange-700 text-white rounded-[2rem] font-black uppercase italic shadow-2xl active:scale-95 transition-all gap-4">
              {loading ? <Loader2 className="animate-spin" /> : <Send size={20} />} ENVIAR PARA O TÉCNICO
            </Button>
          </form>
        </div>
      </main>

      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="rounded-[2.5rem] border-none p-10 bg-white text-left max-w-2xl">
          <DialogHeader className="text-left">
            <DialogTitle className="text-2xl font-black uppercase italic tracking-tighter text-left">Confirmar Pedido?</DialogTitle>
          </DialogHeader>
          <div className="my-6 space-y-4 text-left">
             <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100 grid grid-cols-2 gap-4 text-left">
                <div><Label className="text-[9px] font-black text-slate-400 uppercase">Técnico</Label><p className="font-black text-slate-900 uppercase italic">{techInfo?.professionalData?.name}</p></div>
                <div><Label className="text-[9px] font-black text-slate-400 uppercase">Serviço</Label><p className="font-black text-blue-600 uppercase italic">{formData.serviceArea}</p></div>
             </div>
             <div className="bg-blue-50/50 p-6 rounded-3xl border border-blue-100 flex items-start gap-3 text-left">
                <MapPin className="text-red-500 shrink-0 mt-1" size={20} />
                <div className="text-left">
                   <Label className="text-[9px] font-black text-blue-600 uppercase">Endereço</Label>
                   <p className="font-bold text-slate-700 italic text-sm leading-relaxed uppercase">{formData.address}, {formData.number} {formData.complement && `(${formData.complement})`}<br/>{formData.neighborhood} - {formData.city}/{formData.state}</p>
                </div>
             </div>
          </div>
          <DialogFooter className="flex flex-col md:flex-row gap-3">
            <Button onClick={handleFinalSubmit} disabled={loading} className="w-full h-16 bg-blue-600 hover:bg-blue-700 text-white font-black rounded-2xl uppercase italic shadow-xl">ENVIAR AGORA</Button>
            <Button variant="ghost" onClick={() => setShowConfirmModal(false)} className="h-16 px-8 font-black uppercase italic text-slate-400">Revisar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DirectOrder;